import { z } from 'zod';
// Dashboard filter types
export const DashboardFiltersSchema = z.object({
    period: z.enum(['1d', '7d', '30d', '90d', '1y']).default('7d'),
    userId: z.string().uuid().optional(),
    teamId: z.string().uuid().optional(),
    startDate: z.date().optional(),
    endDate: z.date().optional(),
});
// User activity tracking
export const UserActivitySchema = z.object({
    userId: z.string().uuid(),
    userName: z.string(),
    userRole: z.string(),
    loginTime: z.date(),
    logoutTime: z.date().optional(),
    sessionDuration: z.number(), // in minutes
    totalTimeSpent: z.number(), // in minutes for the period
    loginCount: z.number(),
    lastActiveAt: z.date(),
    ipAddress: z.string().optional(),
    userAgent: z.string().optional(),
});
// Performance metrics
export const UserPerformanceSchema = z.object({
    userId: z.string().uuid(),
    userName: z.string(),
    userRole: z.string(),
    avatar: z.string().optional(),
    // Lead metrics
    leadsAssigned: z.number(),
    leadsConverted: z.number(),
    leadsInProgress: z.number(),
    leadsLost: z.number(),
    conversionRate: z.number(),
    // Task metrics
    tasksAssigned: z.number(),
    tasksCompleted: z.number(),
    tasksPending: z.number(),
    tasksOverdue: z.number(),
    taskCompletionRate: z.number(),
    avgTaskCompletionTime: z.number(), // in hours
    // Appointment metrics
    appointmentsScheduled: z.number(),
    appointmentsCompleted: z.number(),
    appointmentsCancelled: z.number(),
    appointmentsNoShow: z.number(),
    appointmentCompletionRate: z.number(),
    // Activity metrics
    totalActivities: z.number(),
    callsLogged: z.number(),
    emailsSent: z.number(),
    notesCreated: z.number(),
    // Time tracking
    activeHours: z.number(),
    responseTime: z.number(), // average response time in hours
});
// Dashboard overview stats
export const DashboardOverviewSchema = z.object({
    totalUsers: z.number(),
    activeUsers: z.number(),
    totalLeads: z.number(),
    convertedLeads: z.number(),
    totalTasks: z.number(),
    completedTasks: z.number(),
    totalAppointments: z.number(),
    completedAppointments: z.number(),
    totalRevenue: z.number(),
    conversionRate: z.number(),
    taskCompletionRate: z.number(),
    appointmentCompletionRate: z.number(),
});
// Lead analytics
export const LeadAnalyticsSchema = z.object({
    totalLeads: z.number(),
    newLeads: z.number(),
    convertedLeads: z.number(),
    lostLeads: z.number(),
    leadsInProgress: z.number(),
    conversionRate: z.number(),
    avgLeadValue: z.number(),
    totalPipelineValue: z.number(),
    // Stage breakdown
    stageStats: z.array(z.object({
        stage: z.string(),
        stageName: z.string(),
        count: z.number(),
        value: z.number(),
        conversionRate: z.number(),
    })),
    // Source breakdown
    sourceStats: z.array(z.object({
        source: z.string(),
        count: z.number(),
        conversionRate: z.number(),
    })),
    // Trends over time
    leadTrends: z.array(z.object({
        date: z.string(),
        newLeads: z.number(),
        convertedLeads: z.number(),
        lostLeads: z.number(),
    })),
});
// Task analytics
export const TaskAnalyticsSchema = z.object({
    totalTasks: z.number(),
    completedTasks: z.number(),
    pendingTasks: z.number(),
    overdueTasks: z.number(),
    completionRate: z.number(),
    avgCompletionTime: z.number(), // in hours
    // Priority breakdown
    priorityStats: z.array(z.object({
        priority: z.string(),
        count: z.number(),
        completedCount: z.number(),
        completionRate: z.number(),
    })),
    // Type breakdown
    typeStats: z.array(z.object({
        type: z.string(),
        count: z.number(),
        completedCount: z.number(),
        avgCompletionTime: z.number(),
    })),
    // Trends over time
    taskTrends: z.array(z.object({
        date: z.string(),
        created: z.number(),
        completed: z.number(),
        overdue: z.number(),
    })),
});
// Appointment analytics
export const AppointmentAnalyticsSchema = z.object({
    totalAppointments: z.number(),
    scheduledAppointments: z.number(),
    completedAppointments: z.number(),
    cancelledAppointments: z.number(),
    noShowAppointments: z.number(),
    completionRate: z.number(),
    noShowRate: z.number(),
    // Type breakdown
    typeStats: z.array(z.object({
        type: z.string(),
        count: z.number(),
        completedCount: z.number(),
        completionRate: z.number(),
    })),
    // Trends over time
    appointmentTrends: z.array(z.object({
        date: z.string(),
        scheduled: z.number(),
        completed: z.number(),
        cancelled: z.number(),
        noShow: z.number(),
    })),
});
// Attendance analytics
export const AttendanceAnalyticsSchema = z.object({
    totalEmployees: z.number(),
    presentToday: z.number(),
    absentToday: z.number(),
    lateToday: z.number(),
    attendanceRate: z.number(),
    avgCheckInTime: z.string(),
    avgCheckOutTime: z.string(),
    avgWorkHours: z.number(),
    // Department breakdown
    departmentStats: z.array(z.object({
        department: z.string(),
        totalEmployees: z.number(),
        presentCount: z.number(),
        attendanceRate: z.number(),
    })),
    // Trends over time
    attendanceTrends: z.array(z.object({
        date: z.string(),
        present: z.number(),
        absent: z.number(),
        late: z.number(),
        attendanceRate: z.number(),
    })),
});
// HR analytics
export const HRAnalyticsSchema = z.object({
    totalEmployees: z.number(),
    newHires: z.number(),
    activeEmployees: z.number(),
    terminatedEmployees: z.number(),
    // Leave analytics
    totalLeaveRequests: z.number(),
    approvedLeaves: z.number(),
    pendingLeaves: z.number(),
    rejectedLeaves: z.number(),
    leaveApprovalRate: z.number(),
    // Payroll analytics
    totalPayroll: z.number(),
    avgSalary: z.number(),
    payrollGrowth: z.number(),
    // Department breakdown
    departmentStats: z.array(z.object({
        department: z.string(),
        employeeCount: z.number(),
        avgSalary: z.number(),
        leaveUtilization: z.number(),
    })),
});
// Complete dashboard data
export const DashboardDataSchema = z.object({
    overview: DashboardOverviewSchema,
    userActivity: z.array(UserActivitySchema),
    userPerformance: z.array(UserPerformanceSchema),
    leadAnalytics: LeadAnalyticsSchema.optional(),
    taskAnalytics: TaskAnalyticsSchema.optional(),
    appointmentAnalytics: AppointmentAnalyticsSchema.optional(),
    attendanceAnalytics: AttendanceAnalyticsSchema.optional(),
    hrAnalytics: HRAnalyticsSchema.optional(),
    filters: DashboardFiltersSchema,
});
// Individual dashboard responses
export const LeadsDashboardSchema = z.object({
    overview: z.object({
        totalLeads: z.number(),
        newLeads: z.number(),
        convertedLeads: z.number(),
        conversionRate: z.number(),
        pipelineValue: z.number(),
        recentActivities: z.number(),
        // Task metrics
        tasksCompleted: z.number(),
        tasksPending: z.number(),
        // Appointment metrics
        appointmentsScheduled: z.number(),
        // Change percentages compared to previous period
        totalLeadsChange: z.number().optional(),
        newLeadsChange: z.number().optional(),
        conversionRateChange: z.number().optional(),
        pipelineValueChange: z.number().optional(),
        recentActivitiesChange: z.number().optional(),
        tasksCompletedChange: z.number().optional(),
        tasksPendingChange: z.number().optional(),
        appointmentsScheduledChange: z.number().optional(),
    }),
    stageStats: z.array(z.object({
        stage: z.string(),
        count: z.number(),
        value: z.number(),
    })),
    recentLeads: z.array(z.object({
        id: z.string(),
        customerName: z.string(),
        email: z.string().optional(),
        phone: z.string().optional(),
        stage: z.string(),
        assignedTo: z.string().optional(),
        createdAt: z.date(),
        value: z.number().optional(),
    })),
    performanceStats: z.array(UserPerformanceSchema),
});
export const AttendanceDashboardSchema = z.object({
    overview: z.object({
        totalEmployees: z.number(),
        presentToday: z.number(),
        absentToday: z.number(),
        attendanceRate: z.number(),
        avgWorkHours: z.number(),
    }),
    departmentStats: z.array(z.object({
        department: z.string(),
        totalEmployees: z.number(),
        presentCount: z.number(),
        attendanceRate: z.number(),
    })),
    recentAttendance: z.array(z.object({
        id: z.string(),
        employeeName: z.string(),
        checkInTime: z.date().optional(),
        checkOutTime: z.date().optional(),
        status: z.string(),
        workHours: z.number().optional(),
    })),
    trends: z.array(z.object({
        date: z.string(),
        attendanceRate: z.number(),
    })),
});
export const AdmissionsDashboardSchema = z.object({
    overview: z.object({
        totalApplications: z.number(),
        newApplications: z.number(),
        acceptedApplications: z.number(),
        acceptanceRate: z.number(),
        totalRevenue: z.number(),
    }),
    gradeStats: z.array(z.object({
        grade: z.string(),
        applications: z.number(),
        accepted: z.number(),
        acceptanceRate: z.number(),
    })),
    recentApplications: z.array(z.object({
        id: z.string(),
        studentName: z.string(),
        grade: z.string(),
        status: z.string(),
        submittedAt: z.date(),
        fee: z.number().optional(),
    })),
    trends: z.array(z.object({
        date: z.string(),
        applications: z.number(),
        accepted: z.number(),
    })),
});
export const HRDashboardSchema = z.object({
    overview: z.object({
        totalEmployees: z.number(),
        newHires: z.number(),
        pendingLeaves: z.number(),
        totalPayroll: z.number(),
    }),
    leaveStats: z.object({
        totalRequests: z.number(),
        approved: z.number(),
        pending: z.number(),
        rejected: z.number(),
    }),
    departmentStats: z.array(z.object({
        department: z.string(),
        employeeCount: z.number(),
        avgSalary: z.number(),
    })),
    recentActivity: z.array(z.object({
        id: z.string(),
        type: z.string(),
        description: z.string(),
        timestamp: z.date(),
        employee: z.string(),
    })),
});
// Simple user data for dropdowns
export const DashboardUserSchema = z.object({
    userId: z.string().uuid(),
    userName: z.string(),
    userRole: z.string(),
    email: z.string(),
    tenantId: z.string().optional(),
});
export const AnalyticsDashboardSchema = z.object({
    overview: DashboardOverviewSchema,
    userActivity: z.array(UserActivitySchema),
    userPerformance: z.array(UserPerformanceSchema),
    leadAnalytics: LeadAnalyticsSchema,
    taskAnalytics: TaskAnalyticsSchema,
    appointmentAnalytics: AppointmentAnalyticsSchema,
    filters: DashboardFiltersSchema,
    users: z.array(DashboardUserSchema), // Users for dropdown
});
// Dashboard permission mapping
export const DASHBOARD_PERMISSIONS = {
    leads: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP'],
    attendance: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'HR_MANAGER'],
    admissions: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'SALES_MANAGER'],
    hr: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'HR_MANAGER'],
    analytics: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'SALES_MANAGER', 'HR_MANAGER'],
};
// Helper function to check dashboard access
export const canAccessDashboard = (userRole, dashboardType) => {
    return DASHBOARD_PERMISSIONS[dashboardType].includes(userRole);
};
// Helper function to get accessible dashboards for a role
export const getAccessibleDashboards = (userRole) => {
    return Object.entries(DASHBOARD_PERMISSIONS)
        .filter(([_, roles]) => roles.includes(userRole))
        .map(([dashboard]) => dashboard);
};
//# sourceMappingURL=dashboard.js.map